import {
  AnchorHTMLAttributes,
  forwardRef,
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  useCallback,
} from 'react';
import {
  useHref,
  LinkProps as RouterLinkProps,
  useLinkClickHandler,
} from 'react-router-dom';
import { TextLink } from '@atlas-frontend/sdk';

export interface AppLinkProps
  extends Omit<
      AnchorHTMLAttributes<HTMLAnchorElement>,
      'href' | 'children' | 'color'
    >,
    Omit<
      RouterLinkProps,
      keyof Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> | 'children'
    > {
  children?:
    | ReactNode
    | ((childProps: Omit<AppLinkChildProps, 'children'>) => ReactNode);
}

export type AppLinkChildProps = Omit<
  AppLinkProps,
  'replace' | 'state' | 'target' | 'to'
>;

export const AppLink = forwardRef<HTMLAnchorElement, AppLinkProps>(
  (props, ref) => {
    const {
      onClick,
      replace = false,
      state,
      target,
      to,
      children,
      ...rest
    } = props;
    const href = useHref(to);
    const linkClickHandler = useLinkClickHandler(to, {
      replace,
      state,
      target,
    });

    const handleClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
      (event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          linkClickHandler(event as MouseEvent<HTMLAnchorElement>);
        }
      },
      [linkClickHandler, onClick]
    );

    const childProps = {
      ...rest,
      href,
      onClick: handleClick,
      ref,
      target,
    };

    if (typeof children === 'function') {
      return children(childProps);
    }

    return <TextLink {...childProps}>{children}</TextLink>;
  }
);
